import Icon from 'components/common/icon/icon';
import { ReactNode, useContext } from 'react';
import { TranslationContext } from 'contexts/translation-context';
import styled from 'styled-components';
import Link from 'components/common/link/link';
import { PRICE_LIST_TERMS_LINK, RAPNET_TERMS_LINK } from 'constants/urls';

const PageWrapper = styled.div`
  position: relative;
`;

const Header = styled.div`
  top: 0px;
  width: 100%;
  height: 67px;
  background-color: ${props => props.theme.colors.white};
  box-shadow: 0px 2px 10px #0000000d;
  opacity: 1;
  position: fixed;
  display: flex;
  align-items: center;
  z-index: 10;
`;

const HeaderLogo = styled(Icon)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.red};
  opacity: 1;
  font-size: 1.7rem;
  margin-left: 65px;
  @media (max-width: 999px) {
    margin-left: 20px;
  }
`;

const Footer = styled.div`
  width: 100%;
  height: auto;
  min-height: 30px;
  background-color: ${props => props.theme.colors.siteBlack};
  color: ${props => props.theme.colors.white};
  bottom: 0px;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 45px;
  z-index: 3;

  @media (max-width: 999px) {
    flex-direction: column;
    padding: 12px 16px;
    gap: 8px;
  }
`;

const FooterText = styled.span`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.montserratRegular};
  font-size: 10px;
  margin-right: 3px;

  @media (max-width: 999px) {
    font-size: 9px;
  }
`;

const FooterLinks = styled(Link)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-size: 10px;
  margin-right: 3px;
  &:hover {
    color: ${props => props.theme.colors.white};
    cursor: pointer;
  }

  @media (max-width: 999px) {
    font-size: 9px;
  }
`;

const FooterTextLinks = styled(Link)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.montserratRegular};
  font-size: 10px;
  margin-right: 3px;
  text-decoration: underline;
  &:hover {
    color: ${props => props.theme.colors.white};
    cursor: pointer;
  }

  @media (max-width: 999px) {
    font-size: 9px;
  }
`;

const TextWrapper = styled.span`
  display: flex;
  font-family: ${props => props.theme.fonts.montserratRegular};
  flex-wrap: wrap;
  justify-content: center;
  gap: 2px;

  @media (max-width: 999px) {
    justify-content: center;
    text-align: center;
  }
`;

const Divider = styled.span`
  margin-top: -1px;
  font-size: 10px;
  position: relative;
  margin-right: 3px;

  @media (max-width: 999px) {
    display: none;
  }
`;

const FooterLogos = styled(Icon)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-size: 12px;
  margin-right: 5px;

  @media (max-width: 999px) {
    font-size: 10px;
    margin-right: 3px;
  }
`;

const Container = styled.div`
  @media (max-width: 999px) {
    padding-bottom: 80px;
  }
`;

type HeaderFooterProps = { children: ReactNode; isRapnet?: boolean };
export const HeaderFooter = ({ children, isRapnet }: HeaderFooterProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const termsAndConditionsLink = isRapnet ? RAPNET_TERMS_LINK : PRICE_LIST_TERMS_LINK;
  const currentYear = new Date().getFullYear();

  return (
    <PageWrapper>
      <Header>
        <HeaderLogo name={isRapnet ? 'rapnetLogo' : 'rapaportLogo'} />
      </Header>
      <Container>{children}</Container>
      <Footer>
        <FooterLogos name={isRapnet ? 'rapnetLogo' : 'rapaportLogo'} />
        <TextWrapper>
          <FooterText>{TRANSLATION_TEXT.all_content_copyright}.</FooterText>
          <FooterText>{TRANSLATION_TEXT.all_rights_reserved}.</FooterText>
          <Divider>|</Divider>
          <FooterTextLinks href={termsAndConditionsLink} external newTab>
            {TRANSLATION_TEXT.terms_and_conditions}
          </FooterTextLinks>
          <FooterText>{TRANSLATION_TEXT.and}</FooterText>
          <FooterTextLinks href={`https://www.rapnet.com/terms/privacy-policy/`} external newTab>
            {TRANSLATION_TEXT.privacy_policy}.
          </FooterTextLinks>
          <Divider>|</Divider>
          <FooterText>{`Rapaport © ${currentYear}`}</FooterText>
        </TextWrapper>
        <TextWrapper>
          <FooterLinks
            href={`https://www.linkedin.com/company/rapaport/mycompany/`}
            external
            newTab
          >
            <FooterLogos isOther name="linkedin" />
          </FooterLinks>
          <FooterLinks href={`https://www.facebook.com/rapaport`} external newTab>
            <FooterLogos isOther name="facebook" />
          </FooterLinks>
          <FooterLinks href={`https://twitter.com/Rapaport`} external newTab>
            <FooterLogos name="twitterX" />
          </FooterLinks>
        </TextWrapper>
      </Footer>
    </PageWrapper>
  );
};
